import React, { useState } from 'react';
import { Check, Star, Zap, Shield } from 'lucide-react';
import PlanFormModal from './PlanFormModal';

const PricingSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };
  const plans = [
    {
      name: "Básico",
      subtitle: "Até 3 funções",
      price: "799,00",
      originalPrice: null,
      discount: null,
      color: "orange",
      icon: Shield,
      features: [
        "PCMSO (Programa de Controle Médico)",
        "PGR (Programa de Gerenciamento de Riscos)", 
        "eSocial integrado",
        "Até 3 funções simultâneas",
        "Suporte básico",
        "Relatórios padrão"
      ],
      recommended: false
    },
    {
      name: "Profissional",
      subtitle: "Até 6 funções",
      price: "1.099,00",
      originalPrice: "1.399,00",
      discount: "20%",
      color: "green",
      icon: Zap,
      features: [
        "Tudo do plano Básico",
        "Até 6 funções simultâneas",
        "Laudos técnicos avançados",
        "Integração com sistemas externos",
        "Suporte prioritário",
        "Relatórios personalizados",
        "Backup automático"
      ],
      recommended: true
    },
    {
      name: "Empresarial",
      subtitle: "Até 10 funções",
      price: "1.399,00",
      originalPrice: "1.699,00",
      discount: "30%",
      color: "blue",
      icon: Star,
      features: [
        "Tudo do plano Profissional",
        "Até 10 funções simultâneas",
        "Dashboard executivo",
        "API para integrações",
        "Suporte 24/7",
        "Consultoria especializada",
        "Treinamento da equipe",
        "Customizações exclusivas"
      ],
      recommended: false
    },
    {
      name: "Premium",
      subtitle: "Até 15 funções",
      price: "1.899,00",
      originalPrice: "2.299,00",
      discount: "40%",
      color: "purple",
      icon: Star,
      features: [
        "Tudo do plano Empresarial",
        "Até 15 funções simultâneas",
        "Módulos especializados",
        "Inteligência artificial",
        "Gerente de conta dedicado",
        "SLA garantido",
        "Implementação personalizada",
        "Análise de dados avançada"
      ],
      recommended: false
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      orange: {
        bg: "bg-gradient-to-br from-orange-400 to-orange-600",
        text: "text-orange-600",
        button: "bg-orange-600 hover:bg-orange-700",
        border: "border-orange-200"
      },
      green: {
        bg: "bg-gradient-to-br from-green-500 to-green-700",
        text: "text-green-600",
        button: "bg-green-600 hover:bg-green-700",
        border: "border-green-200"
      },
      blue: {
        bg: "bg-gradient-to-br from-blue-500 to-blue-700",
        text: "text-blue-600",
        button: "bg-blue-600 hover:bg-blue-700",
        border: "border-blue-200"
      },
      purple: {
        bg: "bg-gradient-to-br from-purple-500 to-purple-700",
        text: "text-purple-600",
        button: "bg-purple-600 hover:bg-purple-700",
        border: "border-purple-200"
      }
    };
    return colors[color];
  };

  return (
    <section className="pricing-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Planos e Preços</h2>
          <p className="section-description">
            Escolha o plano ideal para sua empresa. Todos incluem o pacote completo 
            PGR/PCMSO/eSocial com diferentes níveis de funcionalidades.
          </p>
        </div>

        <div className="pricing-grid">
          {plans.map((plan, index) => {
            const colorClasses = getColorClasses(plan.color);
            const IconComponent = plan.icon;
            
            return (
              <div 
                key={index} 
                className={`pricing-card ${plan.recommended ? 'pricing-card-recommended' : ''}`}
              >
                {plan.discount && (
                  <div className="discount-badge">
                    {plan.discount} OFF
                  </div>
                )}
                
                {plan.recommended && (
                  <div className="recommended-badge">
                    Mais Popular
                  </div>
                )}

                <div className={`pricing-header ${colorClasses.bg}`}>
                  <div className="pricing-icon">
                    <IconComponent className="icon" />
                  </div>
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-subtitle">{plan.subtitle}</p>
                </div>

                <div className="pricing-body">
                  <div className="price-section">
                    {plan.originalPrice && (
                      <div className="original-price">
                        De R$ {plan.originalPrice}
                      </div>
                    )}
                    <div className="current-price">
                      <span className="currency">R$</span>
                      <span className="amount">{plan.price}</span>
                      <span className="period">/mês</span>
                    </div>
                  </div>

                  <button 
                    className={`plan-button ${colorClasses.button}`}
                    onClick={() => handlePlanSelection(plan)}
                  >
                    Escolher {plan.name}
                  </button>

                  <div className="features-list">
                    <h4 className="features-title">Inclui:</h4>
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="feature-item">
                        <Check className="feature-check" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="pricing-footer">
          <p className="footer-text">
            Todos os planos incluem suporte técnico, atualizações gratuitas e 
            conformidade com as normas regulamentares vigentes.
          </p>
          <div className="contact-info">
            <p>Dúvidas? Entre em contato: <strong>(41) 3643-3336</strong></p>
          </div>
        </div>

        <PlanFormModal 
          isOpen={isModalOpen}
          onClose={closeModal}
          selectedPlan={selectedPlan}
        />
      </div>
    </section>
  );
};

export default PricingSection;